/**
 * Unified Business Metrics API Service
 * =====================================
 * Single source of truth for business metrics — used by Corvana CRM
 * and Client Dashboard. Migrated to central `apiClient`.
 */
import { apiClient } from '@/lib/apiClient';

// In-flight dedup + short TTL cache for the dashboard metrics endpoint.
// Multiple components (Management header, DataHealthBadge, hero, etc.) request
// the same business metrics on mount; without this they fire 10+ identical
// GETs within seconds, which slows resolution and produces inconsistent
// partial payloads. Consumers now share one request + a 15s cached result.
const _metricsCache = new Map(); // key -> { at, data }
const _metricsInflight = new Map(); // key -> Promise
const _METRICS_TTL_MS = 15000;

export async function getDashboardMetrics(businessId, view = 'client') {
  const key = `${businessId}::${view}`;
  const cached = _metricsCache.get(key);
  if (cached && Date.now() - cached.at < _METRICS_TTL_MS) return cached.data;
  if (_metricsInflight.has(key)) return _metricsInflight.get(key);

  const p = apiClient
    .get(`/api/business-metrics/dashboard/${businessId}?view=${view}`)
    .then((res) => {
      _metricsCache.set(key, { at: Date.now(), data: res });
      return res;
    })
    .finally(() => { _metricsInflight.delete(key); });

  _metricsInflight.set(key, p);
  return p;
}

export async function getBusinessKpis(businessId, role = 'owner', industry = 'hospitality') {
  return apiClient.get(`/api/business-metrics/kpis/${businessId}?role=${role}&industry=${industry}`);
}

export async function getWinsOpportunities(businessId) {
  return apiClient.get(`/api/business-metrics/wins-opportunities/${businessId}`);
}

export async function getBusinessSummary(businessId, userName = 'User') {
  return apiClient.get(`/api/business-metrics/summary/${businessId}?user_name=${encodeURIComponent(userName)}`);
}

export const DEMO_BUSINESS_ID = 'demo_the_continental';

export async function getAllDashboardData(businessId, role = 'owner', userName = 'User') {
  try {
    const [metricsRes, kpisRes, winsRes, summaryRes] = await Promise.all([
      getDashboardMetrics(businessId, 'client'),
      getBusinessKpis(businessId, role),
      getWinsOpportunities(businessId),
      getBusinessSummary(businessId, userName),
    ]);
    return {
      success: true,
      metrics: metricsRes.data,
      kpis: kpisRes.kpis,
      wins: winsRes.wins,
      opportunities: winsRes.opportunities,
      summary: summaryRes,
    };
  } catch (error) {
    // Auxiliary dashboard fetches can legitimately fail (e.g. 401 in CRM
    // preview contexts or a business with no connected data). Fail quietly —
    // callers handle the `success: false` result; no console noise.
    return { success: false, error: error?.message };
  }
}
